/**
 * Velocity sampling for drag-scrollable containers.
 *
 * Tracks pointer positions over the last TRAIL_WINDOW_MS and computes release
 * velocity. The caller uses this to launch a Framer Motion inertia animation
 * that continues scroll after the pointer is released.
 *
 * Why Framer Motion animate() and not scrollTo({ behavior: 'smooth' })?
 * ──────────────────────────────────────────────────────────────────────
 * scrollTo({ behavior: 'smooth' }) creates a position discontinuity at
 * drag→momentum handoff: during drag we write scrollLeft every pointermove;
 * smooth scroll restarts its easing from scratch — causing visible jitter on
 * release. Framer Motion's inertia animation picks up at the exact current
 * scrollLeft and continues writing it via onUpdate, so the handoff is seamless.
 *
 * Why animate() and not the drag prop?
 * ──────────────────────────────────────────────────────────────────────
 * motion.div with drag="x" uses CSS transform: translateX(), not scrollLeft,
 * and intercepts touch events globally — hijacking native vertical scroll on
 * mobile. animate() drives a plain number via onUpdate with no DOM side effects.
 *
 * ---
 * Tuning
 * ---
 * TRAIL_WINDOW_MS — rolling window used to sample release velocity.
 *                   Shorter = more responsive to the very last movement.
 */

const TRAIL_WINDOW_MS = 100

type TrailPoint = { x: number; t: number }

/**
 * Tracks pointer positions during a drag for velocity sampling.
 * Keeps a rolling window of the last `TRAIL_WINDOW_MS` milliseconds.
 */
export function createVelocityTrail() {
  const points: TrailPoint[] = []

  return {
    record(x: number) {
      const t = performance.now()
      points.push({ x, t })
      while (points.length > 1 && t - points[0].t > TRAIL_WINDOW_MS) {
        points.shift()
      }
    },

    /** Returns release velocity in px/ms, or null if insufficient data. */
    velocity(): number | null {
      if (points.length < 2) return null
      const first = points[0]
      const last = points[points.length - 1]
      const dt = last.t - first.t
      return dt > 0 ? (last.x - first.x) / dt : null
    },

    clear() {
      points.length = 0
    },
  }
}
